<template>
  <div class="contract-container">
    <v-container>
      <v-row align-content="center" justify="space-between" no-gutters>
        <!-- Left column -->
        <v-col cols="12" lg="8" md="8" class="contract-wrapper"
          :class="`${$vuetify.breakpoint.lgAndUp ? 'mt-6 ' : 'my-4 '}`">
          <div v-if="isContractModelValid && !loaded" class="spinner-wrapper">
            <p class="mt-5 text-body-1 text-center">{{ $t('PDF_GENERATING') }}</p>
            <Spinner class="mt-4" />
          </div>
          <iframe v-else-if="isContractModelValid" :src="iframeSrc" type="application/pdf" width="100%"
            height="100%"></iframe>
        </v-col>

        <SummaryCard>
          <template v-slot:offer-status-button>
            <UpdateOfferStatusButton />
          </template>
          <template v-slot:export-excel-button>
            <ExportToExcelButton />
          </template>
          <template v-slot:signature-buttons>
            <SignatureButtons />
          </template>
        </SummaryCard>
      </v-row>
    </v-container>
    <div ref="html" class="hidden">
      <PdfContent />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { jsPDF } from 'jspdf'
import { GET_OFFER_ITEM, GET_CONTRACT_MODEL } from '@/store/modules/offer/getters'
import { REQUEST_CONTRACT } from '@/store/modules/offer/actions'
import { arialRegular, arialBold } from '@/helpers/fonts'
import { redWatermark } from '@/helpers/watermark-red'
import { OFFER_STATUS, VALIDATION_STATUS, CONTRACT_MODEL } from '@/helpers/constants'
import Spinner from '@/components/spinner/Spinner'
import SummaryCard from '@/components/summary-card'
import UpdateOfferStatusButton from '@/components/buttons/UpdateOfferStatusButton'
import ExportToExcelButton from '@/components/buttons/ExportToExcelButton'
import PdfContent from '@/containers/offer-form/sign-off/PdfContent'
import SignatureButtons from '@/containers/offer-form/sign-off/SignatureButtons'

export default {
  name: 'SignOff',
  components: {
    PdfContent,
    Spinner,
    SummaryCard,
    SignatureButtons,
    UpdateOfferStatusButton,
    ExportToExcelButton,
  },
  data() {
    return {
      doc: '',
      iframeSrc: '',
      loaded: false,
      showSignatureError: false,
      signatureError: '',
      pdfGenerationStarted: false,
    }
  },
  computed: {
    ...mapGetters('offer', [GET_OFFER_ITEM]),
    ...mapGetters('offer', { contractModel: GET_CONTRACT_MODEL }),
    offerId() {
      return this[GET_OFFER_ITEM]('offerId')
    },
    offerStatus() {
      return this[GET_OFFER_ITEM]('status')
    },
    validationStatus() {
      return this[GET_OFFER_ITEM]('validationStatus')
    },
    offerType() {
      return this[GET_OFFER_ITEM]('type')
    },
    isSignatureForbidden() {
      return this.validationStatus !== VALIDATION_STATUS.APPROVED
    },
    isContractModelValid() {
      return Object.values(CONTRACT_MODEL).includes(this.contractModel)
    },
  },
  methods: {
    ...mapActions('offer', [REQUEST_CONTRACT]),
    ...mapMutations('process', ['setPdfFormData']),
    ...mapActions('alerts', ['createAlert']),
    showDownloadedContract(contract) {
      this.loaded = false
      const blobUrl = URL.createObjectURL(contract)
      let formDataPDF = new FormData()
      formDataPDF.append('multipartFile', contract, `${this.offerId}.pdf`)
      this.iframeSrc = blobUrl
      this.setPdfFormData(formDataPDF)
      this.loaded = true
    },
    generatePDF() {
      if (this.isContractModelValid) {
        const vm = this
        let doc = new jsPDF('p', 'mm', 'a4', true)
        doc.addFileToVFS('ARIAL_REGULAR.TTF', arialRegular)
        doc.addFileToVFS('ARIAL_BOLD.TTF', arialBold)
        doc.addFont('ARIAL_REGULAR.TTF', 'arial', 'normal')
        doc.addFont('ARIAL_BOLD.TTF', 'arial', 'bold')
        doc.setFont('arial')
        doc.lineHeightProportion = 2
        let source = this.$refs.html
        doc.html(source, {
          callback: function (doc) {
            if (vm.validationStatus !== VALIDATION_STATUS.APPROVED) {
              doc = vm.addWaterMark(doc)
            }
            vm.doc = doc
            let pageCount = doc.internal.getNumberOfPages()
            doc.deletePage(pageCount)
            let blobPDF = new Blob([doc.output('blob')], {
              type: 'application/pdf',
            })
            let blobUrl = URL.createObjectURL(blobPDF)
            let formDataPDF = new FormData()
            formDataPDF.append('multipartFile', blobPDF, `${vm.offerId}.pdf`)
            vm.iframeSrc = blobUrl
            vm.setPdfFormData(formDataPDF)
            vm.loaded = true
          },
          x: 0,
          y: 0,
          html2canvas: {
            scale: 1,
            useCORS: true,
          },
          imageType: 'JPEG',
          quality: 0.85,
        })
      }
    },
    addWaterMark(doc) {
      const totalPages = doc.internal.getNumberOfPages()
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        doc.addImage(redWatermark, 'PNG', 0, 0, 210, 297)
      }
      return doc
    },

    waitForPdfContent(maxAttempts = 50, interval = 100) {
      return new Promise((resolve) => {
        let attempts = 0
        const check = () => {
          const pages = this.$refs.html?.querySelectorAll('.dinA4')
          if (pages && pages.length > 0) {
            resolve(true)
          } else if (attempts < maxAttempts) {
            attempts++
            setTimeout(check, interval)
          } else {
            resolve(false)
          }
        }
        check()
      })
    },

    async handlePdfGeneration() {
      // Evitar múltiples ejecuciones
      if (this.pdfGenerationStarted) return
      this.pdfGenerationStarted = true
      
      this.$emit('onChangeIsLoading', true)

      // Esperar a que los componentes del PDF estén cargados (lazy-loaded)
      await this.waitForPdfContent()

      if (this.offerStatus !== OFFER_STATUS.SIGNED) {
        this.generatePDF()
      } else {
        const contract = await this[REQUEST_CONTRACT]({
          offerId: this.offerId,
        })

        if ('error' in contract) {
          this.generatePDF()
        } else {
          this.showDownloadedContract(contract)
        }
      }
      this.$emit('onChangeIsLoading', false)
    },
    handleSignatureError(error) {
      // HACK: reset error fields and set new values in next tick in case
      // they had been already set and alert was dismissed
      this.showSignatureError = false
      this.signatureError = ''
      setTimeout(() => {
        this.showSignatureError = true
        if (error === 'updateError') {
          this.signatureError = this.$t('SIGNATURE_UPDATE_ERROR')
        } else if (error === 'cancelError') {
          this.signatureError = this.$t('SIGNATURE_CANCEL_ERROR')
        } else if (error === 'cancelNotAllowed') {
          this.signatureError = this.$t('SIGNATURE_CANCEL_NOT_ALLOWED')
        } else {
          this.showSignatureError = false
        }
      })
    },
  },
  created() {
    this.$emit('onChangeIsContinueForbidden', this.isSignatureForbidden)
  },
  mounted() {
    this.handlePdfGeneration()
    if (!this.isContractModelValid) {
      this.createAlert({
        id: 'invalidContractModel',
        type: 'error',
        contentKey: 'CONTRACT_MODEL_NOT_VALID',
      })
    }
  },
  watch: {
    isSignatureForbidden() {
      this.$emit('onChangeIsContinueForbidden', this.isSignatureForbidden)
    },
  },
}
</script>

<style scoped lang="scss">
.contract-container {
  position: relative;
  overflow: hidden;

  .contract-wrapper {
    align-items: center;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: calc(100vh - 300px);
    padding: 0;
  }

  iframe {
    min-height: 100vh;
  }

  .hidden {
    position: absolute;
    left: 0;
    top: 0;
    opacity: 0;
    pointer-events: none;
    z-index: -1;
  }
}
</style>
